import { _ as _applyDecoratedDescriptor } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { isPresent, isEmpty } from '@ember/utils';
import { guidFor } from '@ember/object/internals';
import { action } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import { bb } from 'billboard.js/dist/billboard.pkgd.js';
import 'billboard.js/dist/billboard.min.css';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  id={{this.chartId}}\n  {{did-insert this.setupChart}}\n  {{did-update this.refreshChart @data @axis @color @dtitle}}\n  class=\'c3-chart-component__\'\n></div>");

var _dec, _class;
function emptyDataFor(data) {
  const empty = {
    label: {
      text: 'No Data'
    }
  };
  if (!data) return {
    columns: [],
    empty
  };
  if (isPresent(data.json)) return {
    ...data,
    json: [],
    empty
  };
  if (isPresent(data.rows)) return {
    ...data,
    rows: [],
    empty
  };
  return {
    ...data,
    columns: [],
    empty
  };
}

/** Class C3Chart — backed by billboard.js */
let BbChartComponent = (_dec = task({
  restartable: true
}), _class = class BbChartComponent extends Component {
  constructor() {
    super(...arguments);
    this.chartId = `c3-${guidFor(this)}`;
    this.config = {};
    this._transition = 350;
    const dataObject = this.data;
    const isSourcePresent = dataObject && (isPresent(dataObject.url) || isPresent(dataObject.json) || isPresent(dataObject.rows) || isPresent(dataObject.columns));

    // Never mutate args: use a new object when the source is absent.
    this.config.data = isSourcePresent ? dataObject : emptyDataFor(dataObject);

    // Only forward args that are explicitly provided — passing `undefined` for
    // optional config keys causes billboard.js to skip internal defaults.
    const optionalProps = ['axis', 'color', 'line', 'bar', 'pie', 'donut', 'gauge', 'grid', 'legend', 'tooltip', 'subchart', 'zoom', 'point', 'regions', 'area', 'size', 'padding', 'title', 'interaction'];
    for (const prop of optionalProps) {
      if (this.args[prop] !== undefined) {
        this.config[prop] = this.args[prop];
      }
    }
    this.transition = this.args.transition;
    this.config.oninit = () => this.args.oninit?.();
    this.config.onrendered = () => this.args.onrendered?.(this.chart);
    this.config.onmouseover = () => this.args.onmouseover?.(this.chart);
    this.config.onmouseout = () => this.args.onmouseout?.(this.chart);
    this.config.onresize = () => this.args.onresize?.(this.chart);
    this.config.onresized = () => this.args.onresized?.(this.chart);
  }
  get data() {
    return this.args.data;
  }
  get axis() {
    return this.args.axis;
  }
  get color() {
    return this.args.color;
  }
  get title() {
    return this.args.dtitle;
  }
  setupChart() {
    // Guard against multiple calls on the same instance (e.g. did-insert
    // firing more than once due to parent re-renders). Destroy the existing
    // chart so billboard.js doesn't accumulate SVG elements in the container.
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch {
        /* already destroyed or broken */
      }
      this.chart = null;
    }
    this.config.data = {
      ...this.config.data
    };
    this.config.bindto = document.getElementById(this.chartId);
    this.chart = bb.generate(this.config);
    if (this.args.c3Chart) this.args.c3Chart(this.chart);
  }
  refreshChart() {
    if (!this.chart) return;
    const element = document.querySelector(`#${this.chartId} .bb-title`);
    if (isPresent(this.title)) {
      if (element) element.innerHTML = this.title.text;
      this.chart.flush();
    }
    if (isEmpty(this.title) || isPresent(this.title) && this.title.refresh) {
      this.chartReload.perform();
    }
  }
  *chartReload() {
    const data = this.data;
    if (!this.chart || !data) return;

    // chart.load() only supports a subset of the generate() config keys.
    // Passing unknown keys (type, types, hide, …) causes billboard.js to
    // treat the call as a full re-initialisation, appending new SVG elements
    // to the container instead of updating the existing chart.
    const LOAD_KEYS = ['columns', 'rows', 'json', 'url', 'xs', 'names', 'colors', 'classes', 'categories', 'unload', 'done', 'type', 'types'];
    const loadParams = {};
    for (const k of LOAD_KEYS) {
      if (data[k] !== undefined) loadParams[k] = data[k];
    }

    // billboard.js load() only adds/updates series; it never removes the ones
    // already on screen. When the new dataset is empty (e.g. API returns
    // {prezzi:null,ore:null,num:null} → columns:[]), load([]) is a no-op and
    // the previous data stays visible. Explicitly unload everything instead.
    const hasData = Array.isArray(loadParams.columns) && loadParams.columns.length > 0 || Array.isArray(loadParams.rows) && loadParams.rows.length > 0 || Array.isArray(loadParams.json) && loadParams.json.length > 0 || isPresent(loadParams.url);
    if (!hasData) {
      this.chart.unload();
      return;
    }

    // URL-fed charts: billboard fetches the URL itself, so the wrapper cannot
    // inspect the response for emptiness. Tell billboard to unload the existing
    // series as part of the load so a now-empty response clears stale data
    // instead of leaving the previous slices/bars on screen.
    if (isPresent(loadParams.url) && loadParams.unload === undefined) {
      loadParams.unload = true;
    }
    if (this.args.unloadDataBeforeChange) {
      this.chart.unload();
      const time = this.transition ?? this._transition;
      yield timeout(time);
      if (!this.chart) return; // chart distrutto durante il timeout
      this.chart.load(loadParams);
    } else {
      this.chart.load(loadParams);
    }
  }
  willDestroy() {
    super.willDestroy(...arguments);
    // [regression-fix] duplicate super.willDestroy() call dropped — parent hook was being invoked twice
    try {
      if (this.chart) this.chart.destroy();
    } catch {
      // chart already destroyed or in a broken state
    }
    this.chart = null;
    this.config = null;
  }
}, _applyDecoratedDescriptor(_class.prototype, "setupChart", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setupChart"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshChart", [action], Object.getOwnPropertyDescriptor(_class.prototype, "refreshChart"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "chartReload", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "chartReload"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbChartComponent);

export { BbChartComponent as default };
//# sourceMappingURL=bb-chart.js.map
